import { useCallback } from "react"

import { useUpdateAssociatedTestTemplate } from "./useUpdateAssociatedTestTemplate"

import type { AerosTableReorderReturn } from "@/components/Tables/Aeros/types/table"
import type { AssociatedTestTemplate } from "@/graphql/codegen/graphql"

interface UseReorderAssociatedTestTemplateParams {
  associatedTestTemplates: AssociatedTestTemplate[]
  refetch: () => void
}

interface UseReorderAssociatedTestTemplateReturn {
  handleReorder: (data: AerosTableReorderReturn<AssociatedTestTemplate>) => Promise<void>
}

export const useReorderAssociatedTestTemplate = ({
  associatedTestTemplates,
  refetch,
}: UseReorderAssociatedTestTemplateParams): UseReorderAssociatedTestTemplateReturn => {
  const { update, error: reorderError } = useUpdateAssociatedTestTemplate({
    errorMessage: "Failed to reorder test template sequence.",
    successMessage: "Associated test templates successfully reordered.",
  })

  const handleReorder = useCallback(
    async (data: AerosTableReorderReturn<AssociatedTestTemplate>) => {
      const { draggedItemIndex, newIndex, newOrder, setNewOrder } = data
      await update({
        associatedTestTemplateId: associatedTestTemplates[draggedItemIndex].associatedTestTemplateId,
        sequenceNumber: newIndex + 1,
      })

      if (reorderError) {
        refetch()
      }

      setNewOrder(
        newOrder?.map((item, i) => ({
          ...item,
          sequenceNumber: i + 1,
        })) as AssociatedTestTemplate[]
      )
    },
    [associatedTestTemplates, refetch, reorderError, update]
  )

  return { handleReorder }
}
